import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import chatService from "../../services/chatService";
import ChatCardRenderer from "./ChatCardRenderer";
import ChatMessageContent from "./ChatMessageContent";
import ChatThinkingIndicator from "./ChatThinkingIndicator";
import { API_CONFIG } from "../../utils/apiConfig";

const INITIAL_SUGGESTIONS = [
  "What is the live stock summary across all categories?",
  "Which shipments are arriving this week?",
  "Are there any containers with demurrage risks?",
  "Show overdue receivables for customers",
  "Show recent purchase bills and suppliers",
  "Check total inventory weight in Yard 1",
  "Show top customers with unpaid invoices",
];

// Helper to parse basic markdown links, bold text, and linebreaks
function renderFormattedMessage(text) {
  if (!text) return "";

  // Split by markdown link pattern [Label](file:///url) or [Label](/url)
  const linkRegex = /\[([^\]]+)\]\((?:file:\/\/\/)?([^)]+)\)/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = linkRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({
        type: "text",
        content: text.substring(lastIndex, match.index),
      });
    }

    let url = match[2];
    if (!url.startsWith("/")) {
      url = "/" + url;
    }

    parts.push({
      type: "link",
      label: match[1],
      url: url,
    });
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push({
      type: "text",
      content: text.substring(lastIndex),
    });
  }

  return (
    <span>
      {parts.map((p, i) => {
        if (p.type === "link") {
          return (
            <Link
              key={i}
              to={p.url}
              className="inline-flex items-center gap-1 font-semibold text-primary-600 underline underline-offset-2 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
            >
              <span>{p.label}</span>
              <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </Link>
          );
        }
        return p.content;
      })}
    </span>
  );
}

export default function ChatDrawer({ isOpen, onClose }) {
  const [messages, setMessages] = useState(() => {
    try {
      const saved = sessionStorage.getItem("rai_chat_history");
      return saved ? JSON.parse(saved) : [];
    } catch (_) {
      return [];
    }
  });
  const [input, setInput] = useState("");
  const [currentQuery, setCurrentQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [suggestedPrompts, setSuggestedPrompts] = useState(INITIAL_SUGGESTIONS);
  const messagesEndRef = useRef(null);
  const chatBodyRef = useRef(null);
  const textareaRef = useRef(null);
  const abortControllerRef = useRef(null);
  const suggestionsContainerRef = useRef(null);
  const prevMessagesLengthRef = useRef(messages.length);

  const savedScrollTopRef = useRef(() => {
    try {
      const saved = sessionStorage.getItem("rai_chat_scroll_top");
      return saved !== null ? Number(saved) : null;
    } catch (_) {
      return null;
    }
  });

  const scrollToBottom = (behavior = "auto") => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTo({
        top: chatBodyRef.current.scrollHeight,
        behavior,
      });
    }
    messagesEndRef.current?.scrollIntoView({ behavior, block: "end" });
  };

  const handleScroll = (e) => {
    if (e.target) {
      savedScrollTopRef.current = e.target.scrollTop;
      try {
        sessionStorage.setItem("rai_chat_scroll_top", String(e.target.scrollTop));
      } catch (_) {}
    }
  };

  // Restore exact position where user was when re-opening the chat drawer
  useEffect(() => {
    if (isOpen) {
      chatService
        .getStatus()
        .then((res) => setStatus(res))
        .catch(() => setStatus({ isConfigured: false }));

      // Auto-focus the input box so the cursor is immediately active
      const focusTimer = setTimeout(() => {
        textareaRef.current?.focus();
      }, 150);

      const restoreScrollPosition = () => {
        if (!chatBodyRef.current) return;
        let pos = typeof savedScrollTopRef.current === "function"
          ? savedScrollTopRef.current()
          : savedScrollTopRef.current;

        if (pos === null) {
          try {
            const stored = sessionStorage.getItem("rai_chat_scroll_top");
            if (stored !== null) pos = Number(stored);
          } catch (_) {}
        }

        if (pos !== null && !isNaN(pos)) {
          chatBodyRef.current.scrollTop = pos;
        } else {
          // Default initial position if never scrolled
          chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
        }
      };

      restoreScrollPosition();
      const t1 = setTimeout(restoreScrollPosition, 40);
      const t2 = setTimeout(restoreScrollPosition, 120);
      return () => {
        clearTimeout(focusTimer);
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }
  }, [isOpen]);

  // Only auto-scroll to bottom when a NEW message is submitted or generated
  useEffect(() => {
    try {
      sessionStorage.setItem("rai_chat_history", JSON.stringify(messages));
    } catch (_) {}

    if (messages.length > prevMessagesLengthRef.current || isLoading) {
      scrollToBottom("smooth");
      setTimeout(() => {
        if (chatBodyRef.current) {
          savedScrollTopRef.current = chatBodyRef.current.scrollTop;
          try {
            sessionStorage.setItem("rai_chat_scroll_top", String(chatBodyRef.current.scrollTop));
          } catch (_) {}
        }
      }, 150);
    }
    prevMessagesLengthRef.current = messages.length;
  }, [messages, isLoading]);

  const handleStop = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsLoading(false);
    setTimeout(() => textareaRef.current?.focus(), 50);
  };

  const handleSend = async (textToSend) => {
    const query = (textToSend || input).trim();
    if (!query) return;

    // If an existing prompt is currently generating, cancel it before starting the new one
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const userMsg = { role: "user", content: query, timestamp: new Date().toISOString() };
    // Only pass prior successful turns as history (excluding errors and cancelled turns)
    const priorHistory = messages
      .filter((m) => !m.isError && !m.isStopped && m.content)
      .slice(-4)
      .map((m) => ({
        role: m.role,
        content: m.content,
      }));

    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setCurrentQuery(query);
    setIsLoading(true);

    try {
      const response = await chatService.sendMessage({
        message: query,
        history: priorHistory,
        signal: abortController.signal,
      });

      const assistantMsg = {
        role: "assistant",
        content: response?.reply || "No response received.",
        structuredContent: response?.structuredContent || null,
        timestamp: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, assistantMsg]);
      if (response?.suggestedFollowUps && response.suggestedFollowUps.length > 0) {
        setSuggestedPrompts(response.suggestedFollowUps);
      }
    } catch (err) {
      const isAborted =
        err?.name === "CanceledError" ||
        err?.code === "ERR_CANCELED" ||
        err?.message === "canceled" ||
        abortController.signal.aborted;

      if (isAborted) {
        const stoppedMsg = {
          role: "assistant",
          content: "Generation stopped. You can enter another prompt.",
          isStopped: true,
          timestamp: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, stoppedMsg]);
        return;
      }

      const rawMessage = String(err?.message || "").trim();
      const isTechnicalError =
        /nvidia|nim|api error|500|completions|failed to fetch|axios/i.test(rawMessage) ||
        !rawMessage;
      const userMessage = isTechnicalError
        ? "Something went wrong, please try again."
        : rawMessage;

      const errorMsg = {
        role: "assistant",
        content: userMessage,
        isError: true,
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
        setIsLoading(false);
      }
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClear = () => {
    setMessages([]);
    setSuggestedPrompts(INITIAL_SUGGESTIONS);
    savedScrollTopRef.current = 0;
    try {
      sessionStorage.removeItem("rai_chat_history");
      sessionStorage.removeItem("rai_chat_scroll_top");
    } catch (_) {}
  };

  return (
    <div
      className={`fixed inset-y-0 right-0 z-50 flex w-full max-w-lg flex-col bg-white shadow-2xl transition-transform duration-300 ease-in-out dark:bg-slate-900 border-l border-slate-200 dark:border-slate-800 ${
        isOpen ? "translate-x-0" : "translate-x-full pointer-events-none"
      }`}
      aria-hidden={!isOpen}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3.5 dark:border-slate-800 bg-white/90 dark:bg-slate-900/90 backdrop-blur-md">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-tr from-primary-600 via-primary-500 to-amber-500 text-white shadow-md shadow-primary-500/25">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-bold text-slate-900 dark:text-white">
                {API_CONFIG.isDemo ? "Anaxis Tech" : "Ferrovant Global"} AI Assistant
              </h3>
              <span
                className={`inline-block h-2 w-2 rounded-full ${
                  status?.isConfigured ? "bg-emerald-500 animate-pulse" : "bg-amber-500"
                }`}
                title={status?.isConfigured ? "Online" : "Connecting..."}
              />
            </div>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">
              Verified Double-Entry Ledger Intelligence
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1.5">
          {messages.length > 0 && (
            <button
              onClick={handleClear}
              title="Clear Conversation"
              className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          )}
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Messages Thread */}
      <div
        ref={chatBodyRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto p-4 space-y-4 bg-surface-light dark:bg-slate-950"
      >
        {messages.length === 0 ? (
          <div className="py-6 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-100/60 text-primary-600 dark:bg-primary-950/40 dark:text-primary-400 mb-3 shadow-sm ring-4 ring-primary-500/10">
              <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <h4 className="text-sm font-bold text-slate-900 dark:text-white">
              {API_CONFIG.isDemo ? "Anaxis Tech" : "Ferrovant Global"} ERP Assistant
            </h4>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 max-w-xs mx-auto leading-relaxed">
              Ask natural language questions to inspect inventory balances, track maritime shipments, view supplier bills, and analyze aging.
            </p>

            <div className="mt-6 text-left">
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-2.5">
                Suggested Quick Inquiries
              </p>
              <div className="flex flex-col gap-2">
                {INITIAL_SUGGESTIONS.map((prompt, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSend(prompt)}
                    className="flex items-center justify-between rounded-xl border border-slate-200/90 bg-white p-3 text-left text-xs font-medium text-slate-700 shadow-sm transition hover:border-primary-500 hover:bg-primary-50/50 hover:text-primary-700 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200 dark:hover:border-primary-500/50 dark:hover:bg-primary-950/30 dark:hover:text-primary-300 group"
                  >
                    <span>{prompt}</span>
                    <span className="text-slate-400 transition-transform group-hover:translate-x-1">→</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          messages.map((m, idx) => (
            <div
              key={idx}
              className={`flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`}
            >
              <div
                className={`rounded-2xl px-4 py-3 text-xs leading-relaxed shadow-xs transition-all ${
                  m.role === "user"
                    ? "max-w-[85%] bg-gradient-to-r from-primary-600 via-primary-500 to-amber-600 text-white rounded-br-none shadow-primary-500/20"
                    : m.isError
                    ? "max-w-[92%] bg-rose-50 text-rose-800 border border-rose-200 dark:bg-rose-950/40 dark:border-rose-900 dark:text-rose-200 rounded-bl-none"
                    : m.isStopped
                    ? "max-w-[92%] bg-slate-100 text-slate-600 border border-slate-200 dark:bg-slate-800/60 dark:border-slate-700 dark:text-slate-300 rounded-bl-none italic"
                    : "w-full max-w-[98%] bg-white text-slate-900 dark:bg-slate-900 dark:text-slate-100 rounded-bl-none border border-slate-200/90 dark:border-slate-800 shadow-xs"
                }`}
              >
                {m.role === "user" ? (
                  <div className="whitespace-pre-wrap font-medium">{m.content}</div>
                ) : m.isError ? (
                  <div className="whitespace-pre-wrap">{m.content}</div>
                ) : m.isStopped ? (
                  <div className="flex items-center gap-2">
                    <span className="flex h-4 w-4 items-center justify-center rounded-sm bg-slate-300 dark:bg-slate-600 text-[9px] font-bold text-slate-700 dark:text-slate-200">
                      ■
                    </span>
                    <span>{m.content}</span>
                  </div>
                ) : (
                  <ChatMessageContent content={m.content} />
                )}
              </div>

              {/* Render dynamic structured cards if attached */}
              {m.structuredContent && (
                <div className="w-full max-w-[95%]">
                  <ChatCardRenderer content={m.structuredContent} />
                </div>
              )}

              <span className="mt-1 text-[9px] text-slate-400 px-1">
                {new Date(m.timestamp || Date.now()).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            </div>
          ))
        )}

        {isLoading && (
          <div className="flex items-start gap-2">
            <ChatThinkingIndicator
              query={currentQuery || messages[messages.length - 1]?.content || ""}
            />
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Suggested Follow-Ups Pills */}
      {messages.length > 0 && suggestedPrompts.length > 0 && !isLoading && (
        <div className="px-4 py-2 border-t border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-900">
          <div
            ref={suggestionsContainerRef}
            onWheel={(e) => {
              if (e.deltaY !== 0) {
                e.currentTarget.scrollLeft += e.deltaY;
              }
            }}
            className="flex gap-2 overflow-x-auto pb-1.5 pt-0.5 slim-scrollbar"
          >
            {suggestedPrompts.map((p, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => handleSend(p)}
                className="shrink-0 whitespace-nowrap rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-[11px] font-medium text-slate-700 shadow-xs transition hover:border-primary-500 hover:bg-primary-50 hover:text-primary-700 dark:border-slate-800 dark:bg-slate-800 dark:text-slate-300 dark:hover:border-primary-400 dark:hover:text-primary-300 active:scale-95 cursor-pointer"
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input Form */}
      <div
        onClick={(e) => {
          if (!e.target.closest("button")) {
            textareaRef.current?.focus();
          }
        }}
        className="border-t border-slate-200 p-3.5 dark:border-slate-800 bg-white dark:bg-slate-900 cursor-text select-none"
      >
        <div
          onClick={(e) => {
            if (!e.target.closest("button")) {
              textareaRef.current?.focus();
            }
          }}
          className="flex items-end gap-2 rounded-xl border border-slate-200 bg-slate-50/80 p-2.5 cursor-text transition-all focus-within:border-primary-500 focus-within:bg-white focus-within:ring-2 focus-within:ring-primary-500/20 dark:border-slate-800 dark:bg-slate-800/50 dark:focus-within:border-primary-500 dark:focus-within:bg-slate-900 shadow-2xs"
        >
          <textarea
            ref={textareaRef}
            rows={1}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              isLoading
                ? "Type a new prompt or click Stop..."
                : "Ask about stock, shipments, suppliers, invoices..."
            }
            className="flex-1 resize-none bg-transparent px-2 py-1 text-xs text-slate-900 placeholder:text-slate-400 focus:outline-none dark:text-white max-h-24 overflow-y-auto cursor-text leading-relaxed"
          />

          <div className="flex items-center gap-1.5 shrink-0">
            {isLoading && (
              <button
                type="button"
                onClick={handleStop}
                title="Stop current generation"
                className="flex h-8 w-8 items-center justify-center rounded-lg bg-rose-500 text-white shadow-md shadow-rose-500/25 transition hover:bg-rose-600 active:scale-95 animate-pulse"
              >
                <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24">
                  <rect x="5" y="5" width="14" height="14" rx="2" />
                </svg>
              </button>
            )}

            <button
              type="button"
              onClick={() => handleSend()}
              disabled={!input.trim()}
              title={isLoading ? "Send new prompt (stops current)" : "Send prompt"}
              className={`flex h-8 w-8 items-center justify-center rounded-lg text-white shadow-md transition active:scale-95 ${
                !input.trim()
                  ? "bg-slate-300 dark:bg-slate-700 cursor-not-allowed opacity-40"
                  : "bg-primary-500 hover:bg-primary-600 shadow-primary-500/20"
              }`}
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </button>
          </div>
        </div>
        <p className="mt-1.5 text-center text-[10px] text-slate-400 dark:text-slate-500">
          Verified Double-Entry Ledger System
        </p>
      </div>
    </div>
  );
}
